// The "community exists" manage view (S2.2 + S2.4). Renders the facility
// community's real fields — name, description, visibility, who-can-post (#1838),
// member_count — and an edit panel (PATCH name / description / visibility /
// post_permission via useUpdateCommunity), gated
// on `community:manage` (server-authoritative). S2.4 adds, as a SIBLING card below
// the metadata (never a card-in-card): an announcement composer (post-as-facility,
// gated on `canPost`) and the community's post feed with a moderator Remove on each
// row (gated on `canModerate`). It owns NO business logic: the member_count comes
// STRAIGHT off the field (a real 0 shows 0; absent ⇒ em-dash — never fabricated),
// a post's author display name degrades to an em-dash when absent, and the raw
// author_id / created_by Firebase UID / community_id are NEVER rendered
// (no-UID-in-UI #1443). A failed save/post/remove keeps the surface honest with an
// error notifications (no phantom success) — RBAC is re-enforced server-side.
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { mediaApi } from "@bw/shared/api";
import { authorLabel } from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import type {
  MediaEmbed,
  PhotoUploadRequest,
  PostResponse,
} from "@bw/shared/types/generated";
import { useRef, useState, type ChangeEvent } from "react";
import { notifications } from "@/app/notifications";
import type { CommunityResponse } from "@/libs/communityApi";
import {
  BusinessCommunityPostCard,
  BusinessCommunityPostMedia,
} from "./business-community-post-card";
import {
  useArchiveCommunity,
  useFacilityCommunityPosts,
  useCreateCommunityPost,
  useDeleteCommunityPost,
  useUpdateCommunity,
  useUpdateCommunityPost,
} from "./use-facility-community";
import { CommunityAdmissionReview } from "./CommunityAdmissionReview";

type Visibility = "public" | "private";
// #1838 — who may post into the community. `all_members` (default) is the
// membership-only behaviour; `moderators_only` is announcement-only (owner /
// moderators / facility operators post; members read + comment). Server-authoritative:
// the backend re-enforces it and returns a real 403 (PostPermissionError).
type PostPermission = "all_members" | "moderators_only";

// The image content types the backend media-upload endpoint allowlists
// (PhotoUploadRequest.content_type). Anything else (e.g. a HEIC) is rejected
// client-side BEFORE the ticket request, surfacing the same honest upload error.
type AllowedContentType = PhotoUploadRequest["content_type"];
const ALLOWED_CONTENT_TYPES: readonly AllowedContentType[] = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function isAllowedContentType(type: string): type is AllowedContentType {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(type);
}

/** A photo that has been uploaded; only `publicUrl` rides the post's media_urls. */
interface PendingPhoto {
  /** A client-minted id for the React key + removal (NOT the server object_key). */
  id: string;
  /** The PERMANENT public url — what goes into media_urls (never the upload_url). */
  publicUrl: string;
}

// Human label for the visibility enum — the raw token is never shown unstyled.
const VISIBILITY_LABELS: Record<string, string> = {
  public: "Public",
  private: "Private",
};

const VisuallyHiddenInput = styled("input")({
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
});

// Human label for the post_permission enum (#1838) — the raw token is never shown
// unstyled in the read-state fact.
const POST_PERMISSION_LABELS: Record<string, string> = {
  all_members: "All members",
  moderators_only: "Only moderators & operators",
};

interface CommunityManageViewProps {
  community: CommunityResponse;
  /** The facility this community belongs to (server-authoritative scope). */
  facilityId: string;
  /** Server-authoritative community:manage grant. False ⇒ no edit affordance. */
  canManage: boolean;
  /** Server-authoritative facility:community grant for posting. False ⇒ no composer. */
  canPost: boolean;
  /** Server-authoritative facility:community grant for moderating. False ⇒ no Remove. */
  canModerate: boolean;
}

export function CommunityManageView({
  community,
  facilityId: _facilityId,
  canManage,
  canPost,
  canModerate,
}: CommunityManageViewProps) {
  // The signed-in operator (the same shared session signal the whole business
  // app uses). Drives ONLY the per-post author-edit gate below: the Edit button
  // shows when the operator IS the post's author. The backend re-enforces
  // author-only on the PATCH (403) — this is a UI affordance, not authorization.
  const { user } = useAuth();
  const viewerUid = user?.uid ?? null;
  const update = useUpdateCommunity(community.community_id);
  const feed = useFacilityCommunityPosts(community.community_id);
  const fetchNextFeedPage = feed.fetchNextPage;
  const createPost = useCreateCommunityPost(community.community_id);
  const removePost = useDeleteCommunityPost(community.community_id);
  const archive = useArchiveCommunity(community.community_id);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(community.name);
  const [description, setDescription] = useState(community.description ?? "");
  const [visibility, setVisibility] = useState<Visibility>(
    community.visibility === "private" ? "private" : "public",
  );
  const [postPermission, setPostPermission] = useState<PostPermission>(
    community.post_permission === "moderators_only"
      ? "moderators_only"
      : "all_members",
  );
  // The announcement composer's draft (the only place the operator's text lives).
  const [announcement, setAnnouncement] = useState("");
  // Photos attached to the announcement — each already uploaded to permanent
  // storage (we keep only the public_url). `uploading` GATES submit so we never
  // post while an upload is in flight (which would drop the in-flight photo);
  // `uploadError` surfaces an honest failure. A monotonic seq mints the React key
  // (decoupled from the server object_key, which we do not trust to be unique).
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const photoSeqRef = useRef(0);
  // The archive-confirmation dialog's open state (controlled so an async archive
  // keeps the dialog until the mutation settles, instead of Radix auto-closing it).
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archivingRef = useRef(false);
  const submittingRef = useRef(false);
  const postingRef = useRef(false);
  // Per-row removal guard, in STATE (not a ref) so the row's Remove button is
  // disabled as soon as a removal starts and STAYS disabled while the removal is
  // in flight or has succeeded (the row is pending-gone until confirmation).
  // This makes a double-click — even two fully-sequential clicks — fire once: the
  // second click lands on a disabled button. On FAILURE the id is cleared so the
  // operator can retry (the post never optimistically vanished).
  const [pendingRemoval, setPendingRemoval] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const openEdit = () => {
    // Re-seed the panel from the latest community fields each open.
    setName(community.name);
    setDescription(community.description ?? "");
    setVisibility(community.visibility === "private" ? "private" : "public");
    setPostPermission(
      community.post_permission === "moderators_only"
        ? "moderators_only"
        : "all_members",
    );
    setEditing(true);
  };

  const handleSave = async () => {
    if (!canManage) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await update.mutateAsync({
        name: trimmed,
        description: description.trim() || null,
        visibility,
        post_permission: postPermission,
      });
      // Success: close the panel + an honest success notification.
      notifications.success("Community updated.");
      setEditing(false);
    } catch (err) {
      // Failure (incl. a backend 403 despite an allowed FE state — the backend is
      // authoritative): an honest error notification, NEVER a phantom success. The panel
      // stays open with the operator's values. Nothing is fabricated.
      notifications.error(
        err instanceof Error ? err.message : "Could not update the community.",
      );
    } finally {
      submittingRef.current = false;
    }
  };

  /**
   * Upload the selected image file(s) to permanent storage, mirroring the player
   * PostComposer's device-upload flow WITHOUT client-side compression
   * (browser-image-compression is not a business-app dependency):
   *   1. reject anything outside the JPEG/PNG/WebP allowlist client-side;
   *   2. request a presigned POST ticket via the shared mediaApi;
   *   3. multipart-POST the file DIRECTLY to object storage (presigned `fields`
   *      first, the file last — S3 policy order), NOT through the BFF;
   *   4. keep the permanent `public_url`.
   * `uploading` is set for the whole run so submit is gated (never post mid-upload).
   * Any failure surfaces honestly (an inline alert + notification), adds NO photo, and
   * leaves the typed draft + already-uploaded photos intact.
   */
  const handleFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const files = Array.from(input.files ?? []);
    // Reset the value so re-selecting the same file re-fires onChange.
    input.value = "";
    if (files.length === 0) return;
    if (uploading) return;

    setUploadError(false);
    setUploading(true);
    try {
      for (const file of files) {
        // The backend allowlists JPEG/PNG/WebP — reject anything else BEFORE the
        // ticket request, surfacing the same honest failure as a network error.
        if (!isAllowedContentType(file.type)) {
          throw new Error(`Unsupported image type: ${file.type || "unknown"}`);
        }

        const ticket = await mediaApi.requestPhotoUpload({
          content_type: file.type,
          content_length: file.size,
        });

        const form = new FormData();
        // S3 POST policy requires the presigned fields FIRST and the file LAST.
        Object.entries(ticket.fields ?? {}).forEach(([key, value]) => {
          form.append(key, value as string);
        });
        form.append("file", file, file.name);

        const response = await fetch(ticket.upload_url, {
          method: ticket.method ?? "POST",
          body: form,
        });
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }

        const photoId = `photo-${photoSeqRef.current++}`;
        setPhotos((prev) => [
          ...prev,
          { id: photoId, publicUrl: ticket.public_url },
        ]);
      }
    } catch (err) {
      // Honest failure: no photo added; the draft + prior photos are untouched.
      setUploadError(true);
      notifications.error(
        err instanceof Error ? err.message : "Could not upload the photo.",
      );
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const handlePost = async () => {
    if (!canPost) return;
    const content = announcement.trim();
    // A post is valid with trimmed text OR at least one uploaded photo (mirrors the
    // player composer). Nothing to post → no-op.
    if (!content && photos.length === 0) return;
    // Never post mid-upload — that would send media_urls without the in-flight photo
    // and SILENTLY DROP it. `posting`/`uploading` also disable the button; guard here
    // too (Enter-to-submit bypasses the button).
    if (uploading) return;
    if (postingRef.current) return;
    postingRef.current = true;
    try {
      // Send media_urls ONLY when a photo is attached, so a text-only post keeps the
      // exact `{ content }` body (no empty media_urls noise).
      const body: { content: string; media_urls?: string[] } = { content };
      if (photos.length > 0) {
        body.media_urls = photos.map((p) => p.publicUrl);
      }
      await createPost.mutateAsync(body);
      // Success: an honest notification and clear the composer. The mutation
      // merges the server-confirmed post into the canonical Feed cache.
      notifications.success("Announcement posted.");
      setAnnouncement("");
      setPhotos([]);
      setUploadError(false);
    } catch (err) {
      // Honest failure — NEVER a phantom success. The typed content + attached photos
      // are preserved so the operator can retry rather than losing their work.
      notifications.error(
        err instanceof Error ? err.message : "Could not post the announcement.",
      );
    } finally {
      postingRef.current = false;
    }
  };

  const handleArchive = async () => {
    if (!canManage) return;
    if (archivingRef.current) return;
    archivingRef.current = true;
    try {
      await archive.mutateAsync();
      // Success: an honest notification + close the dialog. The hook invalidates the
      // facility-community reader, so community-page.tsx's tri-state re-resolves to
      // the archived state — the view is NOT locally faked.
      notifications.success("Community archived.");
      setArchiveOpen(false);
    } catch (err) {
      // Honest failure (incl. a backend 403 for a non-owner — the server is
      // authoritative): an error notification, NO phantom archived state. The dialog stays
      // open so the operator can retry or cancel.
      notifications.error(
        err instanceof Error ? err.message : "Could not archive the community.",
      );
    } finally {
      archivingRef.current = false;
    }
  };

  const handleRemove = async (postId: string) => {
    if (!canModerate) return;
    if (pendingRemoval.has(postId)) return;
    // Mark the row pending BEFORE the await so the button disables immediately and
    // a second (sequential) click is a no-op.
    setPendingRemoval((prev) => new Set(prev).add(postId));
    try {
      await removePost.mutateAsync(postId);
      // Success: the mutation removes the server-confirmed deletion from the
      // canonical Feed cache. Keep the row guard until that render completes.
      notifications.success("Post removed.");
    } catch (err) {
      // Honest failure — the post is NEVER optimistically dropped; it stays on
      // screen. Re-enable the row so the operator can retry.
      setPendingRemoval((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
      notifications.error(
        err instanceof Error ? err.message : "Could not remove the post.",
      );
    }
  };

  const busy = update.isPending;
  const posting = createPost.isPending;
  const archiving = archive.isPending;
  // Post is enabled with text OR ≥1 photo, and NOT while posting or uploading.
  const canSubmitPost =
    (announcement.trim().length > 0 || photos.length > 0) &&
    !posting &&
    !uploading;

  // member_count straight off the field: a real 0 shows "0"; a missing value
  // degrades to an em-dash — never a fabricated count.
  const memberCount =
    typeof community.member_count === "number"
      ? String(community.member_count)
      : "—";

  const posts = feed.posts as PostResponse[];

  return (
    <Stack spacing={2}>
      <Paper
        component="section"
        sx={{ p: { xs: 2, sm: 2.5 } }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
          >
            <Box>
              <Typography component="h2" variant="h6">
                {community.name}
              </Typography>
              {community.description ? (
                <Typography color="text.secondary" variant="body2">
                  {community.description}
                </Typography>
              ) : null}
            </Box>
            {canManage && !editing ? (
              <Button onClick={openEdit} size="small" variant="outlined">
                Edit
              </Button>
            ) : null}
          </Stack>

          {editing ? (
            <Stack spacing={2}>
              <TextField
                disabled={busy}
                id="edit-community-name"
                label="Name"
                onChange={(event) => setName(event.target.value)}
                size="small"
                slotProps={{ htmlInput: { maxLength: 255 } }}
                value={name}
              />

              <TextField
                disabled={busy}
                id="edit-community-description"
                label="Description"
                minRows={3}
                multiline
                onChange={(event) => setDescription(event.target.value)}
                size="small"
                value={description}
              />

              <FormControl disabled={busy} size="small" sx={{ maxWidth: 240 }}>
                <InputLabel id="edit-community-visibility-label">
                  Visibility
                </InputLabel>
                <Select
                  id="edit-community-visibility"
                  label="Visibility"
                  labelId="edit-community-visibility-label"
                  onChange={(event) =>
                    setVisibility(event.target.value as Visibility)
                  }
                  value={visibility}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>

              <FormControl disabled={busy} size="small" sx={{ maxWidth: 320 }}>
                <InputLabel id="edit-community-post-permission-label">
                  Who can post
                </InputLabel>
                <Select
                  id="edit-community-post-permission"
                  label="Who can post"
                  labelId="edit-community-post-permission-label"
                  onChange={(event) =>
                    setPostPermission(event.target.value as PostPermission)
                  }
                  value={postPermission}
                >
                  <MenuItem value="all_members">All members</MenuItem>
                  <MenuItem value="moderators_only">
                    Only moderators &amp; operators
                  </MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                <Button
                  disabled={busy}
                  onClick={() => setEditing(false)}
                  size="small"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  disabled={busy || !name.trim()}
                  onClick={() => void handleSave()}
                  size="small"
                  variant="contained"
                >
                  {busy ? "Saving…" : "Save"}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Box
              component="dl"
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                m: 0,
              }}
            >
              <Box>
                <Typography
                  color="text.secondary"
                  component="dt"
                  variant="body2"
                >
                  Visibility
                </Typography>
                <Box component="dd" sx={{ m: 0, mt: 0.5 }}>
                  <Chip
                    label={
                      VISIBILITY_LABELS[community.visibility] ??
                      community.visibility
                    }
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box>
                {/* #1838 — the current post_permission as an honest read-state fact
                    (a human label; the raw enum token is never shown unstyled). A
                    moderators_only community reads "Only moderators & operators". */}
                <Typography
                  color="text.secondary"
                  component="dt"
                  variant="body2"
                >
                  Who can post
                </Typography>
                <Typography
                  component="dd"
                  sx={{ m: 0, mt: 0.5 }}
                  variant="body2"
                >
                  {POST_PERMISSION_LABELS[community.post_permission] ??
                    community.post_permission}
                </Typography>
              </Box>
              <Box>
                <Typography
                  color="text.secondary"
                  component="dt"
                  variant="body2"
                >
                  Members
                </Typography>
                {/* The count is its own element with EXACT text (so a "42" lookup
                    resolves), wrapped in literal space text nodes ({" "}) on BOTH
                    sides so the FLATTENED textContent reads "Members 42 " — a
                    word-bounded number on both edges. The leading space avoids the
                    label-adjacent "Members42"; the trailing space avoids the
                    next-card-adjacent "42Feed" (the S2.4 Feed card renders right
                    after this, with no whitespace between, which would otherwise
                    destroy the trailing \b and a "0Feed" run reads as neither a
                    bounded 0 nor a bounded count). A missing count degrades to an
                    em-dash, never a fabricated number. */}
                <Typography
                  component="dd"
                  sx={{ m: 0, mt: 0.5 }}
                  variant="body2"
                >
                  {" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {memberCount}
                  </Box>{" "}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Archive (soft-delete) — the most destructive community action, so it
              lives in its own bordered danger row and requires an explicit
              MUI Dialog confirmation with alertdialog semantics (never a native
              confirm()). Gated on the
              community:manage grant client-side; the backend re-enforces the tighter
              owner-only rule (a manager who clicks gets a 403 → honest error notification,
              never a phantom archived state). */}
          {canManage && !editing ? (
            <>
              <Divider />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Typography color="text.secondary" variant="body2">
                  Hides this community from players and closes its feed. This is
                  a soft-archive — the data is retained, not permanently
                  deleted.
                </Typography>
                <Button
                  color="error"
                  onClick={() => setArchiveOpen(true)}
                  size="small"
                  sx={{ flexShrink: 0 }}
                  variant="outlined"
                >
                  Archive community
                </Button>
              </Stack>
              <Dialog
                aria-labelledby="archive-community-title"
                onClose={() => {
                  if (!archiving) setArchiveOpen(false);
                }}
                open={archiveOpen}
                slotProps={{ paper: { role: "alertdialog" } }}
              >
                <DialogTitle id="archive-community-title">
                  Archive this community?
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Archiving soft-deletes “{community.name}”: its status
                    becomes archived, it is hidden from players, and its feed is
                    closed. The community and its data are retained (this is not
                    a permanent hard delete), but members will no longer see it.
                    Only a facility owner can archive.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    disabled={archiving}
                    onClick={() => setArchiveOpen(false)}
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="error"
                    disabled={archiving}
                    onClick={() => void handleArchive()}
                    size="small"
                    variant="contained"
                  >
                    {archiving ? "Archiving…" : "Archive community"}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : null}
        </Stack>
      </Paper>

      {canManage || canModerate ? (
        <Paper
          component="section"
          sx={{ p: { xs: 2, sm: 2.5 } }}
          variant="outlined"
        >
          <CommunityAdmissionReview
            communityId={community.community_id}
            canReview
            joinMode={community.join_mode}
          />
        </Paper>
      ) : null}

      {/* S2.4 — announcement composer + feed. A SIBLING card (never nested in the
          metadata card). The composer shows only with the post grant; the feed is
          always shown (it's the community's wall), with a moderator Remove per row
          only with the moderate grant. */}
      <Paper
        component="section"
        sx={{ p: { xs: 2, sm: 2.5 } }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Feed
          </Typography>
          {canPost ? (
            <Stack spacing={1.5}>
              <TextField
                disabled={posting}
                id="community-announcement"
                label="Announcement"
                minRows={3}
                multiline
                onChange={(event) => setAnnouncement(event.target.value)}
                placeholder="Share an update with your community…"
                size="small"
                value={announcement}
              />

              {/* Attached-photo previews use the same native-MUI media renderer as
                  the feed (referrerPolicy=no-referrer + lazy), so
                  the operator previews a photo exactly as members will see it. Each
                  has a Remove-before-post control. */}
              {photos.length > 0 ? (
                <Stack
                  aria-label="Attached photos"
                  component="ul"
                  spacing={1}
                  sx={{ listStyle: "none", m: 0, p: 0 }}
                >
                  {photos.map((photo) => (
                    <Stack
                      component="li"
                      direction="row"
                      key={photo.id}
                      spacing={1}
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <BusinessCommunityPostMedia
                        inset
                        media={[
                          {
                            media_type: "image",
                            url: photo.publicUrl,
                            youtube_id: null,
                            thumbnail_url: null,
                          } as MediaEmbed,
                        ]}
                      />
                      <Button
                        aria-label="Remove photo"
                        disabled={posting}
                        onClick={() => removePhoto(photo.id)}
                        size="small"
                        variant="outlined"
                      >
                        Remove
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ) : null}

              {/* Honest upload failure — an inline alert (the notification is transient). */}
              {uploadError ? (
                <Alert severity="error">
                  We couldn&apos;t upload that photo. Please try again.
                </Alert>
              ) : null}

              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                {/* The file input is visually hidden but LABEL-associated, so the
                    "Add photo" control is a real, accessible file picker. JPEG/PNG/
                    WebP only (the backend allowlist); multiple allowed. */}
                <Button
                  component="label"
                  disabled={uploading || posting}
                  size="small"
                  variant="outlined"
                >
                  {uploading ? "Uploading…" : "Add photo"}
                  <VisuallyHiddenInput
                    accept="image/jpeg,image/png,image/webp"
                    aria-label="Add photo"
                    disabled={uploading || posting}
                    id="community-photo"
                    multiple
                    onChange={handleFilesSelected}
                    type="file"
                  />
                </Button>
                <Button
                  disabled={!canSubmitPost}
                  onClick={() => void handlePost()}
                  size="small"
                  variant="contained"
                >
                  {posting ? "Posting…" : "Post"}
                </Button>
              </Stack>
            </Stack>
          ) : null}

          {feed.freshnessError ? (
            <Alert
              severity="warning"
              role="status"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={feed.isCheckingFreshness}
                  onClick={() => void feed.checkFreshness()}
                >
                  Try again
                </Button>
              }
            >
              Post updates are temporarily unavailable; loaded posts remain.
            </Alert>
          ) : feed.newPostsAvailable ? (
            <Alert
              severity="info"
              role="status"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={
                    feed.isApplyingFreshness || feed.isCheckingFreshness
                  }
                  onClick={() => void feed.refreshNewPosts()}
                >
                  {feed.isApplyingFreshness ? "Updating…" : "Show new posts"}
                </Button>
              }
            >
              New community posts are available.
            </Alert>
          ) : null}

          {feed.isLoading ? (
            <Stack aria-label="Loading posts" role="status" spacing={1}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton height={48} key={i} variant="rounded" width="100%" />
              ))}
            </Stack>
          ) : feed.isError && !feed.isFetchNextPageError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => feed.refetch()}
                >
                  Try again
                </Button>
              }
            >
              {feed.error instanceof Error
                ? feed.error.message
                : "Failed to load posts."}
            </Alert>
          ) : posts.length === 0 && !feed.hasNextPage ? (
            <Typography color="text.secondary" variant="body2">
              No posts yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack
                component="ul"
                spacing={1.5}
                sx={{ listStyle: "none", m: 0, p: 0 }}
              >
                {posts.map((post) => {
                  // Author-edit gate: the operator can edit ONLY their OWN posts
                  // (viewer uid === post.author_id — a UI affordance; the backend
                  // re-enforces author-only on the PATCH with a 403).
                  const isAuthor =
                    viewerUid !== null && post.author_id === viewerUid;
                  return (
                    <Box component="li" key={post.post_id}>
                      {/* Business owns a dense native-MUI post composition while the
                          shared markdown and media contracts remain authoritative. */}
                      <BusinessCommunityPostCard
                        post={post}
                        actions={
                          isAuthor || canModerate ? (
                            <>
                              {isAuthor ? (
                                <PostEditControl
                                  post={post}
                                  communityId={community.community_id}
                                />
                              ) : null}
                              {canModerate ? (
                                <Button
                                  aria-label={`Remove post by ${authorLabel(
                                    post.author_display_name,
                                    post.author_id,
                                  )}`}
                                  disabled={pendingRemoval.has(post.post_id)}
                                  onClick={() =>
                                    void handleRemove(post.post_id)
                                  }
                                  size="small"
                                  variant="outlined"
                                >
                                  Remove
                                </Button>
                              ) : null}
                            </>
                          ) : undefined
                        }
                      />
                    </Box>
                  );
                })}
              </Stack>
              {feed.isFetchNextPageError ? (
                <Alert
                  severity="error"
                  role="alert"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => fetchNextFeedPage()}
                    >
                      Retry
                    </Button>
                  }
                >
                  We couldn&apos;t load more posts.
                </Alert>
              ) : null}
              {feed.hasNextPage && !feed.isFetchNextPageError ? (
                <Button
                  disabled={feed.isFetchingNextPage || feed.isApplyingFreshness}
                  onClick={() => fetchNextFeedPage()}
                  size="small"
                  sx={{ alignSelf: "center" }}
                  variant="outlined"
                >
                  {feed.isFetchingNextPage ? (
                    <span
                      role="status"
                      aria-live="polite"
                      aria-label="Loading more posts"
                    >
                      Loading more…
                    </span>
                  ) : (
                    "Load more"
                  )}
                </Button>
              ) : !feed.hasNextPage ? (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                  variant="body2"
                >
                  You&apos;re all caught up.
                </Typography>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

/**
 * The per-post author Edit control: a compact outlined MUI "Edit" button (shown
 * ONLY for the operator's OWN posts — the caller gates it) that opens a small
 * business-styled MUI edit dialog: a multiline TextField
 * PREFILLED with the post's current content + Cancel/Save.
 *
 * CONTENT-ONLY by design: the PATCH body is `{ content }` and NEVER media_urls —
 * exclude_unset semantics leave the post's existing photos UNTOUCHED (the player
 * EditPostDialog owns media editing; silently wiping media here would be a
 * destructive lie). Save is disabled while pending, when the content is
 * UNCHANGED (nothing to save), and when the result would be INVALID (a
 * non-repost with no photos needs non-empty trimmed content — the backend would
 * 422). Honest toasts: success announces the edit (the hook installs the exact
 * confirmed response — the "· Edited" marker arrives from the SERVER's
 * edited_at, never fabricated); a failure (403/404/422/network) toasts the error
 * and KEEPS the dialog open with the draft intact. A double-click fires once
 * (submittingRef + the disabled button).
 */
function PostEditControl({
  post,
  communityId,
}: {
  post: PostResponse;
  communityId: string;
}) {
  const updatePost = useUpdateCommunityPost(communityId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(post.content ?? "");
  const submittingRef = useRef(false);

  const saving = updatePost.isPending;
  const trimmed = draft.trim();
  const contentChanged = trimmed !== (post.content ?? "");
  // A non-repost must keep non-empty content OR >= 1 media; the business edit
  // never removes media, so with photos attached an empty quote-less content is
  // still valid. A repost's quote may be empty (a pure repost has no quote).
  const resultValid =
    post.reposted_post != null ||
    trimmed.length > 0 ||
    (post.media ?? []).length > 0;
  const canSave = contentChanged && resultValid && !saving;

  const openDialog = () => {
    // Re-seed the draft from the post's latest content each open.
    setDraft(post.content ?? "");
    setOpen(true);
  };

  const handleSave = async () => {
    if (!canSave) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      // CONTENT-ONLY: exclude_unset leaves media_urls untouched server-side.
      await updatePost.mutateAsync({ postId: post.post_id, content: trimmed });
      // Success: an honest notification + close. The hook invalidated the feed, so the
      // edited content + the server-set edited_at arrive via refetch.
      notifications.success("Post updated.");
      setOpen(false);
    } catch (err) {
      // Honest failure (incl. a backend 403 for a non-author — the server is
      // authoritative): an error notification, the dialog stays open with the draft
      // intact so the operator can retry or cancel. No phantom success.
      notifications.error(
        err instanceof Error ? err.message : "Could not update the post.",
      );
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <>
      <Button
        aria-label={`Edit post by ${authorLabel(
          post.author_display_name,
          post.author_id,
        )}`}
        onClick={openDialog}
        size="small"
        variant="outlined"
      >
        Edit
      </Button>
      <Dialog
        aria-labelledby="edit-community-post-title"
        onClose={() => {
          // Keep the dialog under our control while a save is mid-flight.
          if (!saving) setOpen(false);
        }}
        open={open}
      >
        <DialogTitle id="edit-community-post-title">Edit post</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              disabled={saving}
              id="edit-community-post-content"
              label="Content"
              minRows={4}
              multiline
              onChange={(event) => setDraft(event.target.value)}
              value={draft}
            />
            {(post.media ?? []).length > 0 ? (
              <Typography color="text.secondary" variant="body2">
                Attached photos are kept — editing here changes the text only.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => void handleSave()}
            size="small"
            variant="contained"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
